import { memo, useMemo, useRef, useCallback, useState } from 'react';
import { useTheme, Paper, Typography, Box } from '@mui/material';

import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';

const AXIS_FONT_SIZE = 12;
const LINE_WEIGHT = 2;
const LINE_WEIGHT_HIGHLIGHT = 3.5;
const LINE_OPACITY_DIM = 0.2;

// ─── Типы ────────────────────────────────────────────────────────────────────

export interface MeasurePoint {
    /** Строка даты-времени, напр. "2026-02-24 19:17:49" */
    measure: string;
    /** Массив глубин. Может быть короче values или пустым — см. normalizeDepth. */
    depth: number[];
    /** Значения температур на каждой глубине для данного момента */
    values: number[];
}

export interface HoverPoint {
    depth: number;
    temp: number;
}

// ─── Внутренний тип ───────────────────────────────────────────────────────────

interface ChartEntry {
    time: string; // локальное время в виде строки — для tooltip и onHover
    ts: number; // UTC миллисекунды — для числовой оси X
    [key: string]: unknown;
}

// ─── UTC → локальное время ────────────────────────────────────────────────────

function utcToLocal(utcStr: string): string {
    // Добавляем Z, чтобы Date.parse() трактовал строку как UTC, а не как локальное время
    const d = new Date(utcStr.replace(' ', 'T') + 'Z');
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// ─── Нормализация глубин ──────────────────────────────────────────────────────

/**
 * Расширяет массив depth до длины valuesLength.
 * Каждый недостающий элемент = последний известный + 1.
 * Если depth пустой — заполняется 0, 1, 2, …
 */
function normalizeDepth(depth: number[], valuesLength: number): number[] {
    if (valuesLength === 0) return [];
    const result: number[] = [];
    for (let i = 0; i < valuesLength; i++) {
        if (i < depth.length) {
            result.push(depth[i]);
        } else {
            result.push((result[result.length - 1] ?? -1) + 1);
        }
    }
    return result;
}

// ─── Палитра цветов линий ─────────────────────────────────────────────────────

const CHART_PALETTE = [
    '#D0327B',
    '#4EAAEA',
    '#BFFC4F',
    '#EE8632',
    '#B526F5',
    '#95EDE1',
    '#FEFE54',
    '#EA3323',
    '#68339B',
    '#2F6EBA',
    '#9FCE63',
    '#B02317',
    '#323CFF',
    '#F6C342',
    '#4FAD5B',
    '#A47C4B',
];

// Golden ratio hue distribution для линий сверх палитры — максимальный разброс без повторений
const GOLDEN_RATIO = 0.618033988749895;

function indexToColor(index: number): string {
    if (index < CHART_PALETTE.length) return CHART_PALETTE[index];
    const hue = ((index * GOLDEN_RATIO) % 1) * 360;
    return `hsl(${Math.round(hue)}, 70%, 55%)`;
}

// ─── Подсказка ───────────────────────────────────────────────────────────────

interface TimeTooltipProps {
    active?: boolean;
    payload?: readonly unknown[];
    label?: number;
    lineColors: string[];
    lineCount: number;
    chartData: ChartEntry[];
}

const TOOLTIP_COLUMN_SIZE = 10;

function TimeTooltip({ active, label, lineColors, lineCount, chartData }: TimeTooltipProps) {
    const theme = useTheme();
    if (!active || label == null) return null;

    const entry = chartData.find((d) => d.ts === label);
    if (!entry) return null;

    const rows: React.ReactNode[] = [];
    for (let i = 0; i < lineCount; i++) {
        const temp = entry[`v${i}`] as number | null | undefined;
        const depth = entry[`d${i}`] as number | null | undefined;
        if (temp == null) continue;
        rows.push(
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.15 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: lineColors[i], flexShrink: 0 }} />
                <Typography variant="caption" color="text.primary" noWrap>
                    {depth ?? i}&nbsp;м.&nbsp;:&nbsp;{temp.toFixed(2)}&nbsp;°С
                </Typography>
            </Box>,
        );
    }
    if (!rows.length) return null;

    const columns: React.ReactNode[][] = [];
    for (let i = 0; i < rows.length; i += TOOLTIP_COLUMN_SIZE) {
        columns.push(rows.slice(i, i + TOOLTIP_COLUMN_SIZE));
    }

    return (
        <Paper
            elevation={3}
            sx={{
                px: 1.5,
                py: 1,
                border: `1px solid ${theme.palette.primary.dark}`,
                pointerEvents: 'none',
            }}
        >
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                {entry.time}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
                {columns.map((col, ci) => (
                    <Box key={ci}>{col}</Box>
                ))}
            </Box>
        </Paper>
    );
}

// ─── Компонент ───────────────────────────────────────────────────────────────

export interface TimeChartProps {
    data: MeasurePoint[];
    /** Высота графика в пикселях или процентах. По умолчанию 400. */
    height?: number | `${number}%`;
    /**
     * Вызывается при движении мыши над графиком — передаёт текущие значения
     * для каждой линии. При выходе мыши — передаёт значения последней точки данных.
     */
    onHover?: (points: HoverPoint[], label: string) => void;
    /**
     * Если передан, соответствующая линия рисуется жирно, остальные — полупрозрачно.
     */
    highlightIndex?: number;
}

export default memo(function TimeChart({ data, height = 400, onHover, highlightIndex }: TimeChartProps) {
    const theme = useTheme();
    const lastFiredLabel = useRef<number | null>(null);
    const [isLocked, setIsLocked] = useState(false);
    const [lockedLabel, setLockedLabel] = useState<number | null>(null);
    const chartHeight = height;

    // Максимальное количество линий среди всех точек
    const lineCount = useMemo(() => (data.length === 0 ? 0 : Math.max(...data.map((d) => d.values.length))), [data]);

    // Нормализованные глубины для каждой точки
    const normalizedData = useMemo(
        () => data.map((p) => ({ ...p, _depths: normalizeDepth(p.depth, p.values.length) })),
        [data],
    );

    // Данные для Recharts: одна запись на точку времени
    const chartData = useMemo<ChartEntry[]>(
        () =>
            normalizedData.map((p) => {
                const entry: ChartEntry = {
                    time: utcToLocal(p.measure),
                    ts: new Date(p.measure.replace(' ', 'T') + 'Z').getTime(),
                };
                for (let i = 0; i < lineCount; i++) {
                    entry[`v${i}`] = p.values[i] ?? null;
                    entry[`d${i}`] = p._depths[i] ?? null;
                }
                return entry;
            }),
        [normalizedData, lineCount],
    );

    const lineColors = useMemo<string[]>(
        () => Array.from({ length: lineCount }, (_, i) => indexToColor(i)),
        [lineCount],
    );

    // Значения для callback при выходе мыши — последняя точка данных
    const lastPointHover = useMemo<HoverPoint[]>(() => {
        if (!normalizedData.length) return [];
        const last = normalizedData[normalizedData.length - 1];
        return last.values.map((temp, i) => ({ depth: last._depths[i] ?? i, temp }));
    }, [normalizedData]);

    const handleMouseMove = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => {
            if (isLocked) return;
            const label = state?.activeLabel as number | undefined;
            if (label == null || label === lastFiredLabel.current) return;
            lastFiredLabel.current = label;

            const entry = chartData.find((d) => d.ts === label);
            if (!entry) return;

            const points: HoverPoint[] = [];
            for (let i = 0; i < lineCount; i++) {
                const temp = entry[`v${i}`] as number | null;
                const depth = entry[`d${i}`] as number | null;
                if (temp !== null && depth !== null) points.push({ depth, temp });
            }
            onHover?.(points, entry.time);
        },
        [chartData, lineCount, onHover, isLocked],
    );

    const handleMouseLeave = useCallback(() => {
        if (isLocked) return;
        const lastLabel = chartData[chartData.length - 1]?.time ?? '';
        lastFiredLabel.current = null;
        onHover?.(lastPointHover, lastLabel);
    }, [onHover, lastPointHover, chartData, isLocked]);

    const handleChartClick = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => {
            if (isLocked) {
                setIsLocked(false);
                setLockedLabel(null);
                const lastLabel = chartData[chartData.length - 1]?.time ?? '';
                lastFiredLabel.current = null;
                onHover?.(lastPointHover, lastLabel);
            } else {
                const label = state?.activeLabel as number | undefined;
                if (label == null) return;
                setIsLocked(true);
                setLockedLabel(label);
            }
        },
        [isLocked, chartData, onHover, lastPointHover],
    );

    const xAxisConfig = useMemo(() => {
        const pad2 = (n: number) => String(n).padStart(2, '0');

        if (chartData.length < 2) {
            return {
                formatter: (v: number) => {
                    const d = new Date(v);
                    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
                },
                label: 'время',
                ticks: undefined as number[] | undefined,
            };
        }

        const start = chartData[0].ts;
        const end = chartData[chartData.length - 1].ts;
        const spanMs = end - start;
        const DAY = 86_400_000;

        const TICK_COUNT = 10;
        const step = spanMs / (TICK_COUNT - 1);
        const ticks = Array.from({ length: TICK_COUNT }, (_, i) => Math.round(start + i * step));

        if (spanMs < 2 * DAY) {
            return {
                formatter: (v: number) => {
                    const d = new Date(v);
                    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
                },
                label: 'время',
                ticks,
            };
        }
        if (spanMs < 60 * DAY) {
            return {
                formatter: (v: number) => {
                    const d = new Date(v);
                    return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}`;
                },
                label: 'дата',
                ticks,
            };
        }
        const MONTHS = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
        return {
            formatter: (v: number) => {
                const d = new Date(v);
                return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
            },
            label: 'месяц',
            ticks,
        };
    }, [chartData]);

    const hasHighlight = highlightIndex !== undefined;

    const gridColor = theme.palette.divider;
    const textColor = theme.palette.text.secondary;
    const bgColor = theme.palette.background.paper;

    // Порядок линий: выделенная рисуется последней → поверх остальных
    const lineOrder = useMemo(() => {
        const indices = Array.from({ length: lineCount }, (_, i) => i);
        if (!hasHighlight) return indices;
        return [
            ...indices.filter((i) => i !== highlightIndex),
            ...(highlightIndex! < lineCount ? [highlightIndex!] : []),
        ];
    }, [lineCount, hasHighlight, highlightIndex]);

    if (data.length === 0) {
        return (
            <Box
                sx={{
                    height,
                    display: 'flex',
                    m: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                }}
            >
                <Typography variant="body2" color="text.disabled">
                    Нет данных для отображения
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100%', '& *:focus': { outline: 'none' }, cursor: isLocked ? 'pointer' : 'default' }}>
            <Box sx={{ width: '100%', height: chartHeight }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={chartData}
                        margin={{ top: 12, right: 16, bottom: 8, left: 4 }}
                        onMouseMove={handleMouseMove}
                        onMouseLeave={handleMouseLeave}
                        onClick={handleChartClick}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />

                        <XAxis
                            dataKey="ts"
                            type="number"
                            scale="time"
                            domain={['dataMin', 'dataMax']}
                            ticks={xAxisConfig.ticks}
                            orientation="top"
                            tickFormatter={xAxisConfig.formatter}
                            tick={{ fill: textColor, fontSize: AXIS_FONT_SIZE }}
                            tickLine={{ stroke: gridColor }}
                            axisLine={{ stroke: gridColor }}
                            label={{
                                value: xAxisConfig.label,
                                position: 'insideBottomRight',
                                fill: textColor,
                                fontSize: AXIS_FONT_SIZE,
                                dy: 2,
                            }}
                        />

                        <YAxis
                            type="number"
                            domain={['auto', 'auto']}
                            tick={{ fill: textColor, fontSize: AXIS_FONT_SIZE }}
                            tickLine={{ stroke: gridColor }}
                            axisLine={{ stroke: gridColor }}
                            width={42}
                            label={{
                                value: '°С',
                                position: 'insideTopLeft',
                                fill: textColor,
                                fontSize: AXIS_FONT_SIZE,
                                dx: 4,
                                dy: 12,
                            }}
                        />

                        <ReferenceLine y={0} stroke={gridColor} strokeWidth={1.2} />
                        {isLocked && lockedLabel != null && (
                            <ReferenceLine x={lockedLabel} stroke={theme.palette.primary.main} strokeWidth={1.5} />
                        )}

                        <Tooltip
                            content={(props) => (
                                <TimeTooltip
                                    active={props.active}
                                    payload={props.payload}
                                    label={props.label as number | undefined}
                                    lineColors={lineColors}
                                    lineCount={lineCount}
                                    chartData={chartData}
                                />
                            )}
                            cursor={
                                isLocked
                                    ? false
                                    : {
                                          stroke: theme.palette.primary.light,
                                          strokeWidth: 1.5,
                                          strokeDasharray: '6 4',
                                      }
                            }
                            wrapperStyle={{ transition: 'opacity 150ms ease' }}
                        />

                        {lineOrder.map((i) => {
                            const isHighlighted = !hasHighlight || i === highlightIndex;
                            return (
                                <Line
                                    key={i}
                                    type="linear"
                                    dataKey={`v${i}`}
                                    stroke={lineColors[i]}
                                    strokeWidth={hasHighlight && isHighlighted ? LINE_WEIGHT_HIGHLIGHT : LINE_WEIGHT}
                                    strokeOpacity={hasHighlight && !isHighlighted ? LINE_OPACITY_DIM : 1}
                                    isAnimationActive={false}
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    dot={
                                        isLocked
                                            ? (props: any) => {
                                                  const { cx, cy, payload } = props;
                                                  if (payload.ts !== lockedLabel) return null;
                                                  return (
                                                      <circle
                                                          cx={cx}
                                                          cy={cy}
                                                          r={4}
                                                          stroke={lineColors[i]}
                                                          strokeWidth={2}
                                                          fill={bgColor}
                                                      />
                                                  );
                                              }
                                            : false
                                    }
                                    activeDot={
                                        isLocked
                                            ? false
                                            : isHighlighted
                                              ? { r: 4, stroke: lineColors[i], strokeWidth: 2, fill: bgColor }
                                              : false
                                    }
                                />
                            );
                        })}
                    </LineChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
});
